/**
 * React Query Key Factories
 * 
 * Use these keys consistently across all useQuery and invalidateQueries calls
 * to ensure proper cache invalidation and data synchronization.
 * 
 * Pattern: Each resource has an 'all' key for lists and a function for specific items.
 */

export const queryKeys = {
  // Cart
  cart: {
    all: ['cart'],
    product: ['cart', 'product'],
    voucher: ['cart', 'voucher'],
    media: ['cart', 'media'],
    counts: ['cart', 'counts'],
  },

  // Wishlist
  wishlist: {
    all: ['wishlist'],
    products: ['wishlist', 'products'],
  },

  // Products
  products: {
    all: ['products'],
    list: (filters) => ['products', 'list', filters],
    detail: (id) => ['products', 'detail', id],
    affordable: ['products', 'affordable'],
  },

  // Vouchers
  vouchers: {
    all: ['vouchers'],
    list: (filters) => ['vouchers', 'list', filters],
    detail: (id) => ['vouchers', 'detail', id],
  },

  // Media
  media: {
    all: ['media'],
    list: (filters) => ['media', 'list', filters],
    detail: (id) => ['media', 'detail', id],
  },

  // Orders
  orders: {
    all: ['orders'],
    list: (filters) => ['orders', 'list', filters],
    detail: (id) => ['orders', 'detail', id],
    tracking: (id) => ['orders', 'tracking', id],
    seller: ['orders', 'seller'],
    buyer: ['orders', 'buyer'],
  },

  // Order Requests (Aggregator)
  orderRequests: {
    all: ['orderRequests'],
    list: (filters) => ['orderRequests', 'list', filters],
    detail: (id) => ['orderRequests', 'detail', id],
  },

  // Order Summary (legacy, maps to Order Request)
  orderSummary: {
    detail: (id) => ['orderSummary', id],
  },

  // Company
  company: {
    all: ['company'],
    detail: (id) => ['company', 'detail', id],
    logged: ['company', 'logged'],
    type: ['company', 'type'],
    fullData: (id) => ['company', 'fullData', id],
    members: (companyId) => ['company', 'members', companyId],
    memberDetail: (memberId) => ['company', 'member', memberId],
  },

  // Profile
  profile: {
    completion: ['profile', 'completion'],
    user: ['profile', 'user'],
  },

  // Auth / User
  auth: {
    user: ['auth', 'user'],
    loggedInUser: ['auth', 'loggedInUser'],
  },

  // Wallet
  wallet: {
    all: ['wallet'],
    balance: ['wallet', 'balance'],
    usableBalance: ['wallet', 'usableBalance'],
    statement: ['wallet', 'statement'],
  },

  // Notifications
  notifications: {
    all: ['notifications'],
    unread: ['notifications', 'unread'],
  },

  // Invoices
  invoices: {
    all: ['invoices'],
    detail: (id) => ['invoices', 'detail', id],
    byOrder: (orderId) => ['invoices', 'byOrder', orderId],
  },

  // Membership / Plans
  membership: {
    all: ['membership'],
    plans: ['membership', 'plans'],
    current: ['membership', 'current'],
  },

  // Calculations
  calculations: {
    cartProduct: ['calculations', 'cart', 'product'],
    cartVoucher: ['calculations', 'cart', 'voucher'],
    cartMedia: ['calculations', 'cart', 'media'],
    cartItem: (itemId) => ['calculations', 'cartItem', itemId],
  },

  // Credit Batch
  creditBatch: {
    all: ['creditBatch'],
  },

  // JusPay
  juspay: {
    paymentMethods: ['juspay', 'paymentMethods'],
  },

  // Categories
  categories: {
    all: ['categories'],
  },

  // Ready Stock / Deals
  deals: {
    readyStockProducts: ['deals', 'readyStock', 'products'],
    readyStockMedia: ['deals', 'readyStock', 'media'],
    readyStockVouchers: ['deals', 'readyStock', 'vouchers'],
  },
};

export default queryKeys;
